import { useMemo, useRef, useState } from "react";
import "./KeywordsInput.css";
import { useTranslation } from "react-i18next";

/**
 * @param {object} props
 * @param {string} props.label
 * @param {string[]} props.value
 * @param {(next: string[]) => void} props.onChange
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.dedupe=true]
 * @param {"default"|"resourceForm"} [props.variant]
 * @param {boolean} [props.showHint=true]
 */
export default function KeywordsInput({
  label,
  value = [],
  onChange,
  placeholder = "Type a keyword and press Enter",
  disabled = false,
  dedupe = true,
  variant = "default",
  showHint = true,
}) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);
  const { t } = useTranslation();

  const chips = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const splitTokens = (txt) =>
    (txt || "")
      .split(/[\n,;]+/g)
      .map((s) => s.trim())
      .filter(Boolean);

  const commitTokens = (tokens) => {
    if (!tokens || tokens.length === 0) return;

    let next = [...chips, ...tokens];

    if (dedupe) {
      const seen = new Set();
      next = next.filter((k) => {
        const key = k.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    onChange?.(next);
  };

  const removeAt = (idx) => {
    const next = chips.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  const commitDraft = () => {
    const tokens = splitTokens(draft);
    if (tokens.length === 0) return;
    commitTokens(tokens);
    setDraft("");
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      commitDraft();
      return;
    }
    if (e.key === "Backspace" && draft.length === 0 && chips.length > 0) {
      e.preventDefault();
      removeAt(chips.length - 1);
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;

    const text = e.clipboardData?.getData("text") ?? "";
    const tokens = splitTokens(text);

    if (tokens.length >= 2) {
      e.preventDefault();
      commitTokens(tokens);
      setDraft("");
    }
  };

  return (
    <div className="kw-field">
      {label ? <p className="kw-label">{label}</p> : null}

      <div
        className={`kw-wrap ${variant === "resourceForm" ? "kw-wrap--resourceForm" : ""} ${disabled ? "kw-wrap--disabled" : ""}`}
        onClick={() => inputRef.current?.focus()}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if ((event.key === "Enter" || event.key === " ") && !disabled) {
            event.preventDefault();
            inputRef.current?.focus();
          }
        }}
        role="button"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        {chips.map((k, idx) => (
          <span key={`${k}-${idx}`} className="kw-chip">
            <span className="kw-chipText">{k}</span>
            {!disabled && (
              <button
                type="button"
                className="kw-chipRemove"
                onClick={(ev) => {
                  ev.stopPropagation();
                  removeAt(idx);
                }}
                aria-label={`Remove ${k}`}
              >
                ×
              </button>
            )}
          </span>
        ))}

        <input
          ref={inputRef}
          className="kw-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => {
            if (!disabled) commitDraft();
          }}
          placeholder={chips.length === 0 ? placeholder : ""}
          disabled={disabled}
          inputMode="text"
          autoComplete="off"
        />
      </div>

      {!disabled && showHint && (
        <div className="kw-hint">
          {t("wizard.steps.dataset.press")}
          <b>{t("wizard.steps.dataset.enter")}</b>{" "}
          {t("wizard.steps.dataset.add")} • {t("wizard.steps.dataset.use")}
        </div>
      )}
    </div>
  );
}
